import {
  AddRounded,
  ApartmentRounded,
  ArrowBackRounded,
  ChevronRightRounded,
  LogoutRounded,
  MedicalServicesRounded,
  MenuRounded,
  PaymentsRounded,
  PeopleRounded,
  PersonRounded,
  PetsRounded,
  QuestionAnswerRounded,
  CloseRounded,
  FileDownloadRounded,
  PlaceRounded,
  SettingsRounded,
  ReportProblemRounded,
} from '@mui/icons-material'
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material'
import * as React from 'react'
import { FC } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { accountCanEdit } from '../domain/Account/accountCanEdit'
import { fullName } from '../domain/Account/AccountDisplay'
import { getBestCustomerTypeLabel } from '../domain/Customer/CustomerDisplay'
import { AccountDtoPermission, AccountDtoRoles, CustomerDtoTypes } from '../interactors/gen/backendClient'
import { useAccountsStore } from '../store/AccountsStore'
import { getCustomerProfilePhotoSrc } from '../utils/S3-links'
import { colors } from './theme'
import { PATHS } from './PATHS'
import logo from './logo.svg'
import { AnimalExportPopUp } from './Animals/AnimalExportPopUp'
import { MemberExportSheet } from './Members/MemberExportSheet'
import { GettingStartedModal } from './common/GettingStartedPanel'
import { VideoPopIn } from './utils/VideoPopIn'
import { PaymentsIntroModal } from './Payments/PaymentsIntroModal'
import { backendClient } from '../interactors/clients/client'
import { AuthService } from '../interactors/services/AuthService'

interface Props {
  customerTypes: CustomerDtoTypes[] | undefined
}

// TODO: replace with the final URL when provided.
const REPORT_PROBLEM_URL = 'https://51lloy792xs.typeform.com/to/QjVXM2me'

interface DashboardPageContext {
  title: string
  showBackButton: boolean
  isOverview: boolean
}

interface NavigationItem {
  label: string
  icon: React.ReactNode
  onClick: () => void
  isActive: boolean
}

const ASSOCIATION_PROFILE_HINT_COUNT_KEY = 'petso_associationProfileHintCount'

const baseRoutes = [
  PATHS.association.absolute,
  PATHS.animals.absolute,
  PATHS.suiviBenevole.absolute,
  PATHS.suiviVeterinaire.absolute,
  PATHS.suiviAdoptant.absolute,
  PATHS.carte.absolute,
]

export const getDashboardPageContext = (
  pathname: string,
  search: string,
  customerTypes: CustomerDtoTypes[] | undefined
): DashboardPageContext => {
  const adoptionTab = new URLSearchParams(search).get('tab')
  const customerTypeLabel = getBestCustomerTypeLabel(customerTypes, { my: true })

  if (pathname === PATHS.association.absolute) {
    return { title: customerTypeLabel, showBackButton: false, isOverview: true }
  }
  if (pathname === PATHS.animals.absolute) {
    return { title: 'Animaux', showBackButton: false, isOverview: true }
  }
  if (pathname === PATHS.suiviBenevole.absolute) {
    return { title: 'Membres', showBackButton: false, isOverview: true }
  }
  if (pathname === PATHS.suiviVeterinaire.absolute) {
    return { title: 'Vétérinaires', showBackButton: false, isOverview: true }
  }
  if (pathname === PATHS.carte.absolute) {
    return { title: 'La carte', showBackButton: false, isOverview: true }
  }
  if (pathname === PATHS.suiviAdoptant.absolute) {
    return {
      title: adoptionTab === 'settings' ? 'Suivi des règlements' : 'Adoptants',
      showBackButton: false,
      isOverview: true,
    }
  }

  if (pathname.startsWith(PATHS.association.absolute)) {
    return { title: customerTypeLabel, showBackButton: true, isOverview: false }
  }
  if (pathname.startsWith(PATHS.animals.absolute)) {
    if (pathname.includes(PATHS.ajouterAnimal.relative)) {
      return { title: 'Ajouter un animal', showBackButton: true, isOverview: false }
    }
    if (pathname.includes(PATHS.multiEdition.relative)) {
      return { title: 'Modifier plusieurs animaux', showBackButton: true, isOverview: false }
    }
    return { title: 'Fiche animal', showBackButton: true, isOverview: false }
  }
  if (pathname.startsWith(PATHS.suiviBenevole.absolute)) {
    if (pathname.includes(PATHS.ajouterBenevole.relative)) {
      return { title: 'Ajouter un membre', showBackButton: true, isOverview: false }
    }
    return { title: 'Profil membre', showBackButton: true, isOverview: false }
  }
  if (pathname.startsWith(PATHS.suiviVeterinaire.absolute)) {
    if (pathname.includes(PATHS.ajouterVeterinaire.relative)) {
      return { title: 'Ajouter un veterinaire', showBackButton: true, isOverview: false }
    }
    return { title: 'Profil veterinaire', showBackButton: true, isOverview: false }
  }
  if (pathname.startsWith(PATHS.suiviAdoptant.absolute)) {
    if (pathname.includes(PATHS.ajouterAdoptant.relative)) {
      return { title: 'Ajouter un adoptant', showBackButton: true, isOverview: false }
    }
    if (pathname.includes(PATHS.assignerAnimal.relative)) {
      return { title: 'Associer un animal', showBackButton: true, isOverview: false }
    }
    return { title: 'Fiche adoptant', showBackButton: true, isOverview: false }
  }

  return {
    title: 'CRM',
    showBackButton: !baseRoutes.includes(pathname),
    isOverview: false,
  }
}

const sidebarWidth = 272
const sidebarCollapsedWidth = 84
const shellSurfaceColor = '#EEF2F8'

const getNavItemStyles = (isActive: boolean, isCollapsed = false) => ({
  minHeight: 56,
  borderRadius: 3.5,
  px: isCollapsed ? 1.25 : 2,
  py: 1.55,
  mb: 1,
  backgroundColor: isActive ? '#F8F3E7' : 'transparent',
  color: isActive ? colors.cello : '#687188',
  '&:hover': {
    backgroundColor: isActive ? '#F8F3E7' : 'rgba(255, 255, 255, 0.45)',
  },
  '& .MuiListItemIcon-root': {
    minWidth: isCollapsed ? 0 : 36,
    color: isActive ? colors.cello : '#7A8197',
    justifyContent: 'center',
  },
  '& .MuiListItemText-primary': {
    fontSize: '0.98rem',
    fontWeight: isActive ? 700 : 600,
  },
})

export const ResponsiveAppBar: FC<Props> = ({ customerTypes }) => {
  const accountsStore = useAccountsStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(false)
  const [anchorElAssociationMenu, setAnchorElAssociationMenu] = React.useState<null | HTMLElement>(null)
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null)
  const [anchorElFaq, setAnchorElFaq] = React.useState<null | HTMLElement>(null)
  const [animalExportSignal, setAnimalExportSignal] = React.useState<number | null>(null)
  const [isPaymentsIntroOpen, setIsPaymentsIntroOpen] = React.useState(false)
  const [isVideoOpen, setIsVideoOpen] = React.useState(false)
  const [isGettingStartedOpen, setIsGettingStartedOpen] = React.useState(false)
  const [isAssociationProfileHintVisible, setIsAssociationProfileHintVisible] = React.useState(false)

  const pageContext = getDashboardPageContext(location.pathname, location.search, customerTypes)
  const customer = accountsStore.connectedCustomer
  const customerId = accountsStore.connectedAccount?.customerId
  const customerTypeLabel = getBestCustomerTypeLabel(customerTypes, { my: true })
  const customerDisplay = customer?.name || customerId || customerTypeLabel
  const connectedUserName = accountsStore.connectedAccount ? fullName(accountsStore.connectedAccount) : 'Mon profil'
  const profileImageKey = customer?.profileImageKey
  const customerLogoSrc = customerId ? `https://logo-customer-images.s3.eu-west-3.amazonaws.com/${customerId}.jpg` : ''
  const avatarSrc = (profileImageKey && getCustomerProfilePhotoSrc(profileImageKey)) || customerLogoSrc || undefined
  const adoptionTab = new URLSearchParams(location.search).get('tab')
  const planLabel =
    customer?.subscription?.status === 'active'
      ? customer.subscription.plan === 'premium'
        ? 'Premium'
        : customer.subscription.plan === 'essential'
        ? 'Essentiel +'
        : 'Basic'
      : 'Basic'

  const connectedAccount = accountsStore.connectedAccount
  const canShowAssociationProfileHint =
    !isSidebarCollapsed &&
    !profileImageKey &&
    connectedAccount?.permission === AccountDtoPermission.Administrator &&
    connectedAccount.roles.includes(AccountDtoRoles.Member)

  const shouldShowAssociationProfileHint = canShowAssociationProfileHint && isAssociationProfileHintVisible

  React.useEffect(() => {
    if (!canShowAssociationProfileHint) {
      setIsAssociationProfileHintVisible(false)
      return
    }

    const hintDisplayCount = parseInt(localStorage.getItem(ASSOCIATION_PROFILE_HINT_COUNT_KEY) || '0', 10)
    if (hintDisplayCount >= 3) {
      setIsAssociationProfileHintVisible(false)
      return
    }

    let timeoutId: number | null = null

    const scheduleHint = () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId)
      }

      timeoutId = window.setTimeout(() => {
        const latestHintDisplayCount = parseInt(localStorage.getItem(ASSOCIATION_PROFILE_HINT_COUNT_KEY) || '0', 10)
        if (latestHintDisplayCount >= 3) {
          return
        }

        localStorage.setItem(ASSOCIATION_PROFILE_HINT_COUNT_KEY, String(latestHintDisplayCount + 1))
        setIsAssociationProfileHintVisible(true)
      }, 10000)
    }

    const resetInactivityTimer = () => {
      if (!isAssociationProfileHintVisible) {
        scheduleHint()
      }
    }

    scheduleHint()

    const events: Array<keyof WindowEventMap> = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart']
    events.forEach((eventName) => window.addEventListener(eventName, resetInactivityTimer, { passive: true }))

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId)
      }
      events.forEach((eventName) => window.removeEventListener(eventName, resetInactivityTimer))
    }
  }, [canShowAssociationProfileHint, isAssociationProfileHintVisible])

  const handleDismissAssociationProfileHint = () => {
    setIsAssociationProfileHintVisible(false)
  }

  const handleMobileDrawerToggle = () => {
    setMobileOpen((currentValue) => !currentValue)
  }

  const handleOpenAssociationMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElAssociationMenu(event.currentTarget)
  }

  const handleCloseAssociationMenu = () => {
    setAnchorElAssociationMenu(null)
  }

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget)
  }

  const handleCloseUserMenu = () => {
    setAnchorElUser(null)
  }

  const handleOpenFaqMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElFaq(event.currentTarget)
  }

  const handleCloseFaqMenu = () => {
    setAnchorElFaq(null)
  }

  const handleNavigate = (path: string) => {
    handleCloseAssociationMenu()
    handleCloseUserMenu()
    setMobileOpen(false)
    navigate(path)
  }

  const handleAddAnimalNavigate = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    handleNavigate(PATHS.ajouterAnimal.absolute)
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    })
  }

  const handleOpenAnimalExportFromMenu = () => {
    handleCloseAssociationMenu()
    handleCloseUserMenu()
    setAnimalExportSignal((prev) => (prev ?? 0) + 1)
  }

  const handlePaymentsNavigate = () => {
    // Keep legacy behavior: payments/settings live under adoptants follow-up settings tab.
    handleCloseAssociationMenu()
    handleCloseUserMenu()
    setMobileOpen(false)
    if (!accountsStore.connectedCustomer?.stripeAccountId) {
      setIsPaymentsIntroOpen(true)
      return
    }
    // Stripe is enabled -> take the user straight to Stripe dashboard (new tab), same as the settings screen CTA.
    window.open('https://dashboard.stripe.com/login', '_blank')
  }

  const isStripeConfigured = Boolean(accountsStore.connectedCustomer?.stripeAccountId)

  const handleOpenStripeDashboard = () => {
    handleCloseAssociationMenu()
    handleCloseUserMenu()
    window.open('https://dashboard.stripe.com/login', '_blank')
  }

  const handleOpenPaymentsIntro = () => {
    handleCloseFaqMenu()
    setIsPaymentsIntroOpen(true)
  }

  const handleOpenGettingStarted = () => {
    handleCloseFaqMenu()
    setIsGettingStartedOpen(true)
  }

  const handleLogout = () => {
    handleCloseAssociationMenu()
    handleCloseUserMenu()
    setMobileOpen(false)
    AuthService.logout()
  }

  const navigationItems: NavigationItem[] = [
    {
      label: 'Animaux',
      icon: <PetsRounded />,
      onClick: () => handleNavigate(PATHS.animals.absolute),
      isActive: location.pathname.startsWith(PATHS.animals.absolute),
    },
    {
      label: 'Adoptants',
      icon: <PeopleRounded />,
      onClick: () => handleNavigate(PATHS.suiviAdoptant.absolute),
      isActive: location.pathname.startsWith(PATHS.suiviAdoptant.absolute) && adoptionTab !== 'settings',
    },
    {
      label: 'Suivi des règlements',
      icon: <PaymentsRounded />,
      onClick: handlePaymentsNavigate,
      isActive: location.pathname === PATHS.suiviAdoptant.absolute && adoptionTab === 'settings',
    },
  ]
  const associationMenuTriggerActive =
    location.pathname.startsWith(PATHS.association.absolute) ||
    location.pathname.startsWith(PATHS.suiviBenevole.absolute) ||
    location.pathname.startsWith(PATHS.suiviVeterinaire.absolute) ||
    location.pathname.startsWith(PATHS.carte.absolute)
  const shouldShowAnimalExport = pageContext.isOverview && location.pathname === PATHS.animals.absolute
  const shouldShowMemberExport =
    pageContext.isOverview &&
    (location.pathname === PATHS.suiviBenevole.absolute || location.pathname === PATHS.suiviVeterinaire.absolute)

  const hasConnectedAccountId = Boolean(accountsStore.connectedAccount?.id)
  const canAddAnimals = Boolean(accountsStore.connectedAccount && accountCanEdit(accountsStore.connectedAccount))
  const handleRequestPayment = () => {
    // Keep legacy mechanics: the yellow banner CTA navigated to "add adopter" (entry point for payments flow).
    backendClient.post('/customers/tracking', { event: 'Clique sur la bannière démarrer adoption' })
    handleNavigate(PATHS.ajouterAdoptant.absolute)
  }

  const sidebarContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: shellSurfaceColor,
      }}
    >
      <Box sx={{ px: 2, pt: 0.75, pb: 0.75, position: 'relative' }}>
        <IconButton
          aria-label="Fermer le menu"
          onClick={handleMobileDrawerToggle}
          size="small"
          sx={{
            display: { xs: 'inline-flex', md: 'none' },
            position: 'absolute',
            top: 10,
            right: 10,
            color: '#7A8197',
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            border: '1px solid rgba(122, 129, 151, 0.18)',
            backdropFilter: 'blur(10px)',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.85)' },
          }}
        >
          <CloseRounded fontSize="small" />
        </IconButton>
        {/* Intentionally no burger icon when sidebar is deployed (requested). */}

        <Stack direction="row" alignItems="flex-start" spacing={1.5}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'white',
              border: '1px solid #E4E8F2',
              boxShadow: '0 1px 2px rgba(32, 56, 100, 0.04)',
              flexShrink: 0,
              cursor: { xs: 'default', md: 'pointer' },
            }}
            role="button"
            tabIndex={0}
            onClick={() => {
              // Desktop: click logo to collapse/expand the sidebar (requested behavior)
              if (window.matchMedia && window.matchMedia('(min-width:900px)').matches) {
                setIsSidebarCollapsed((prev) => !prev)
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                if (window.matchMedia && window.matchMedia('(min-width:900px)').matches) {
                  setIsSidebarCollapsed((prev) => !prev)
                }
              }
            }}
          >
            <Box component="img" src={logo} alt="Petso" sx={{ width: 28, height: 28 }} />
          </Box>
          {!isSidebarCollapsed && (
            <Stack spacing={0.85} sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: colors.cello,
                  fontFamily: 'Insaniburger',
                  fontSize: '1.1rem',
                  letterSpacing: '.18rem',
                }}
              >
                PETSO
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Chip
                  label={planLabel}
                  onClick={() => handleNavigate('/association?tab=functionalities')}
                  size="small"
                  sx={{
                    height: 24,
                    backgroundColor: planLabel === 'Basic' ? '#F3F5FA' : '#FFF5D6',
                    color: colors.cello,
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    border: `1px solid ${planLabel === 'Basic' ? '#DEE3EF' : '#F0DE9D'}`,
                    borderRadius: '999px',
                    cursor: 'pointer',
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<QuestionAnswerRounded fontSize="small" />}
                  onClick={handleOpenFaqMenu}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '999px',
                    height: 28,
                    px: 1.25,
                    borderColor: '#D7DDEA',
                    color: colors.cello,
                    backgroundColor: 'white',
                    fontWeight: 700,
                    boxShadow: 'none',
                    '&:hover': {
                      borderColor: '#C8D0E0',
                      backgroundColor: '#F8F9FC',
                      boxShadow: 'none',
                    },
                    '& .MuiButton-startIcon': {
                      marginRight: 0.5,
                    },
                  }}
                >
                  FAQ
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      </Box>

      <Box sx={{ px: 1.5, pt: 0.5, pb: 1.25 }}>
        {!isSidebarCollapsed && (
          <Typography
            sx={{
              px: 2,
              mb: 0.75,
              color: '#8A90A4',
              fontSize: '0.72rem',
              fontWeight: 700,
              letterSpacing: '.08rem',
              textTransform: 'uppercase',
            }}
          >
            Suivi général
          </Typography>
        )}
        <List disablePadding>
          {navigationItems.map((item) => (
            <Tooltip key={item.label} title={isSidebarCollapsed ? item.label : ''} placement="right">
              <ListItemButton
                selected={item.isActive}
                onClick={item.onClick}
                sx={getNavItemStyles(item.isActive, isSidebarCollapsed)}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                {!isSidebarCollapsed && (
                  <>
                    <ListItemText primary={item.label} />
                    {item.label === 'Suivi des règlements' && !isStripeConfigured && (
                      <Tooltip title="Action requise: configurer Stripe Connect" placement="right">
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 22,
                            height: 22,
                            borderRadius: '999px',
                            backgroundColor: 'rgba(252, 180, 2, 0.14)',
                            border: '1px solid rgba(252, 180, 2, 0.25)',
                            color: '#FCB402',
                            flexShrink: 0,
                          }}
                        >
                          <ReportProblemRounded sx={{ fontSize: 16 }} />
                        </Box>
                      </Tooltip>
                    )}
                  </>
                )}
              </ListItemButton>
            </Tooltip>
          ))}
          <Tooltip title={isSidebarCollapsed ? 'Asso & Membres' : ''} placement="right">
            <ListItemButton
              selected={associationMenuTriggerActive}
              onClick={handleOpenAssociationMenu}
              sx={getNavItemStyles(associationMenuTriggerActive, isSidebarCollapsed)}
            >
              <ListItemIcon>
                <SettingsRounded />
              </ListItemIcon>
              {!isSidebarCollapsed && <ListItemText primary="Asso & Membres" />}
            </ListItemButton>
          </Tooltip>
        </List>
      </Box>

      <Box sx={{ flexGrow: 1 }} />

      <Box sx={{ px: 1.25, pb: 0.75, flexShrink: 0 }}>
        {isSidebarCollapsed ? (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Tooltip title="Recevoir un paiement" placement="right">
              <IconButton
                aria-label="Recevoir un paiement"
                onClick={handleRequestPayment}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '999px',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  border: '1px solid #E4E8F2',
                  color: '#7A8197',
                  '&:hover': { backgroundColor: 'white' },
                }}
              >
                <PaymentsRounded />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Box sx={{ mx: 0.5 }}>
            <Button
              variant="outlined"
              color="inherit"
              fullWidth
              onClick={handleRequestPayment}
              startIcon={<AddRounded />}
              sx={{
                textTransform: 'none',
                borderRadius: 3.5,
                minHeight: 40,
                py: 0.75,
                fontWeight: 700,
                borderColor: 'rgba(122, 129, 151, 0.24)',
                color: colors.cello,
                backgroundColor: 'rgba(255, 255, 255, 0.72)',
                boxShadow: 'none',
                '&:hover': {
                  borderColor: 'rgba(122, 129, 151, 0.35)',
                  backgroundColor: 'white',
                },
              }}
            >
              Recevoir un paiement
            </Button>
          </Box>
        )}
      </Box>

      {canAddAnimals && (
        <Box sx={{ px: 1.25, pb: 0.75, flexShrink: 0 }}>
          {isSidebarCollapsed ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <IconButton
                aria-label="Ajouter un animal"
                onClick={handleAddAnimalNavigate}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '999px',
                  backgroundColor: '#FCB402',
                  color: 'white',
                  boxShadow: '0 8px 18px rgba(32, 56, 100, 0.18)',
                  '&:hover': { backgroundColor: '#E9A900' },
                }}
              >
                <AddRounded />
              </IconButton>
            </Box>
          ) : (
            <Button
              fullWidth
              variant="contained"
              startIcon={<AddRounded />}
              onClick={handleAddAnimalNavigate}
              sx={{
                textTransform: 'none',
                borderRadius: 3.5,
                fontWeight: 700,
                py: 0.75,
                minHeight: 40,
                boxShadow: 'none',
              }}
            >
              Ajouter un animal
            </Button>
          )}
        </Box>
      )}

      <Box
        sx={{
          px: 1.25,
          pb: 1.25,
          pt: 1.25,
          borderTop: '1px solid rgba(122, 129, 151, 0.1)',
          flexShrink: 0,
        }}
      >
        {isSidebarCollapsed ? (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <IconButton
              aria-label="Menu association"
              onClick={handleOpenUserMenu}
              sx={{
                width: 44,
                height: 44,
                borderRadius: '999px',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                border: '1px solid #E4E8F2',
                color: '#7A8197',
                '&:hover': { backgroundColor: 'white' },
              }}
            >
              <SettingsRounded />
            </IconButton>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, position: 'relative' }}>
            <Box
              onClick={handleOpenUserMenu}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                p: 1.25,
                borderRadius: 4,
                backgroundColor: '#F9FBFF',
                border: '1px solid #E4E8F2',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
                position: 'relative',
                zIndex: shouldShowAssociationProfileHint ? 1202 : 'auto',
                boxShadow: shouldShowAssociationProfileHint
                  ? '0 18px 42px rgba(20, 32, 60, 0.18)'
                  : '0 0 0 rgba(20, 32, 60, 0)',
                '&:hover': {
                  backgroundColor: '#EEF2F8',
                },
              }}
            >
              <Avatar alt={customerDisplay} src={avatarSrc} sx={{ width: 44, height: 44 }} />
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography sx={{ color: colors.cello, fontSize: '0.95rem', fontWeight: 700 }} noWrap>
                  {connectedUserName}
                </Typography>
                <Typography sx={{ color: '#7A8197', fontSize: '0.83rem', fontWeight: 500 }} noWrap>
                  {customerDisplay}
                </Typography>
              </Box>
              <ChevronRightRounded sx={{ color: '#8A90A4', flexShrink: 0 }} />
            </Box>

            {shouldShowAssociationProfileHint && (
              <Box
                onClick={() => handleNavigate(PATHS.association.absolute)}
                sx={{
                  position: 'relative',
                  zIndex: 1202,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.45,
                  p: 1.25,
                  ml: 0.5,
                  mr: 0.25,
                  borderRadius: 3,
                  backgroundColor: 'rgba(255,255,255,0.92)',
                  border: '1px solid #DCE5F2',
                  boxShadow: '0 12px 28px rgba(20, 32, 60, 0.1)',
                  cursor: 'pointer',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.73rem',
                    lineHeight: 1.2,
                    fontWeight: 800,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: '#698DAC',
                  }}
                >
                  Pour bien commencer
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.82rem',
                    lineHeight: 1.35,
                    fontWeight: 600,
                    color: colors.cello,
                  }}
                >
                  Ajoutez une photo et complétez les informations de votre association.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  )

  return (
    <>
      {shouldShowAssociationProfileHint && (
        <>
          <Box
            sx={{
              display: { xs: 'none', md: 'block' },
              position: 'fixed',
              inset: 0,
              zIndex: 1190,
              backgroundColor: 'rgba(244, 248, 255, 0.56)',
              backdropFilter: 'blur(5px)',
            }}
          />
          <IconButton
            onClick={handleDismissAssociationProfileHint}
            sx={{
              display: { xs: 'none', md: 'inline-flex' },
              position: 'fixed',
              top: 18,
              right: 18,
              zIndex: 1205,
              width: 42,
              height: 42,
              borderRadius: '999px',
              backgroundColor: 'rgba(255,255,255,0.92)',
              border: '1px solid #DCE5F2',
              color: '#7A8197',
              boxShadow: '0 14px 30px rgba(20, 32, 60, 0.12)',
              '&:hover': {
                backgroundColor: 'white',
              },
            }}
          >
            <CloseRounded />
          </IconButton>
        </>
      )}
      <PaymentsIntroModal open={isPaymentsIntroOpen} onClose={() => setIsPaymentsIntroOpen(false)} />
      <AppBar
        position="fixed"
        elevation={0}
        color="inherit"
        sx={{
          display: { xs: 'block', md: 'none' },
          backgroundColor: shellSurfaceColor,
          borderBottom: '1px solid rgba(122, 129, 151, 0.12)',
          backdropFilter: 'blur(12px)',
        }}
      >
        <Toolbar sx={{ minHeight: '64px !important', px: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flexGrow: 1, minWidth: 0 }}>
            {pageContext.showBackButton && (
              <IconButton onClick={() => navigate(-1)} size="small" sx={{ color: colors.cello }}>
                <ArrowBackRounded />
              </IconButton>
            )}
            <IconButton onClick={handleMobileDrawerToggle} size="small" sx={{ color: colors.cello }}>
              <MenuRounded />
            </IconButton>
            <Typography
              sx={{
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: colors.cello,
                fontSize: '1.15rem',
                fontWeight: 700,
              }}
            >
              {pageContext.title}
            </Typography>
          </Stack>
          <Avatar alt={customerDisplay} src={avatarSrc} sx={{ width: 34, height: 34 }} />
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          width: isSidebarCollapsed ? sidebarCollapsedWidth : sidebarWidth,
          minWidth: isSidebarCollapsed ? sidebarCollapsedWidth : sidebarWidth,
          height: '100%',
          border: '1px solid #E4E8F2',
          borderRight: 'none',
          borderRadius: '28px 0 0 28px',
          backgroundColor: shellSurfaceColor,
          overflow: 'hidden',
        }}
      >
        {sidebarContent}
      </Box>

      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={handleMobileDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: 'min(392px, 100vw)',
            maxWidth: '100vw',
            backgroundColor: shellSurfaceColor,
            borderRadius: '0 28px 28px 0',
          },
        }}
      >
        {sidebarContent}
      </Drawer>

      <Menu
        id="dashboard-association-menu"
        anchorEl={anchorElAssociationMenu}
        open={Boolean(anchorElAssociationMenu)}
        onClose={handleCloseAssociationMenu}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 286,
            borderRadius: 4,
            border: '1px solid rgba(122, 129, 151, 0.14)',
            backgroundColor: 'white',
            boxShadow: '0 18px 42px rgba(20, 32, 60, 0.12)',
            overflow: 'visible',
            p: 0.75,
            '& .MuiMenuItem-root': {
              borderRadius: 3,
              px: 1.25,
              py: 1,
              color: colors.cello,
              '&:hover': {
                backgroundColor: '#F6F8FC',
              },
            },
          },
        }}
        MenuListProps={{
          sx: {
            backgroundColor: 'white',
            p: 0,
          },
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <Box sx={{ px: 1.25, pt: 0.75, pb: 1 }}>
          <Typography sx={{ color: colors.cello, fontSize: '0.98rem', fontWeight: 800 }}>Asso & Membres</Typography>
          <Typography sx={{ color: '#7A8197', fontSize: '0.82rem', fontWeight: 500 }}>
            Accédez aux réglages de votre association, à vos membres et à vos outils.
          </Typography>
        </Box>

        <MenuItem onClick={() => handleNavigate(PATHS.association.absolute)}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <ApartmentRounded fontSize="small" />
            <Typography>Mon asso</Typography>
          </Stack>
        </MenuItem>

        <Box
          sx={{
            position: 'relative',
            pl: 1.25,
            py: 0.25,
            my: 0.25,
            backgroundColor: 'white',
            '& .MuiMenuItem-root': {
              backgroundColor: 'white',
              '&.Mui-focusVisible': { backgroundColor: 'white' },
              '&.Mui-selected': { backgroundColor: 'white' },
            },
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              bottom: 8,
              left: 14,
              width: 1,
              backgroundColor: '#D7DDEA',
              borderRadius: '999px',
            }}
          />

          <MenuItem onClick={() => handleNavigate(PATHS.suiviBenevole.absolute)} sx={{ pl: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <PeopleRounded fontSize="small" />
              <Typography>Membres</Typography>
            </Stack>
          </MenuItem>

          <MenuItem onClick={() => handleNavigate(PATHS.suiviVeterinaire.absolute)} sx={{ pl: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <MedicalServicesRounded fontSize="small" />
              <Typography>Vétérinaires</Typography>
            </Stack>
          </MenuItem>

          <MenuItem onClick={() => handleNavigate(PATHS.carte.absolute)} sx={{ pl: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <PlaceRounded fontSize="small" />
              <Typography>La carte</Typography>
            </Stack>
          </MenuItem>
        </Box>

        {customer?.stripeAccountId && (
          <MenuItem onClick={handleOpenStripeDashboard}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <PaymentsRounded fontSize="small" />
              <Typography>Stripe</Typography>
            </Stack>
          </MenuItem>
        )}

        {shouldShowAnimalExport && (
          <MenuItem onClick={handleOpenAnimalExportFromMenu}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <FileDownloadRounded fontSize="small" />
              <Typography>Export</Typography>
            </Stack>
          </MenuItem>
        )}
        {shouldShowMemberExport && <MemberExportSheet menuItem onBeforeOpen={handleCloseAssociationMenu} />}

        {hasConnectedAccountId && (
          <MenuItem
            onClick={() => handleNavigate(`${PATHS.suiviBenevole.absolute}/${accountsStore.connectedAccount?.id}`)}
          >
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <PersonRounded fontSize="small" />
              <Typography>{`Mon profil${connectedUserName ? ` (${connectedUserName})` : ''}`}</Typography>
            </Stack>
          </MenuItem>
        )}

        <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid rgba(122, 129, 151, 0.14)' }}>
          <MenuItem onClick={handleLogout}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <LogoutRounded fontSize="small" />
              <Typography>Se déconnecter</Typography>
            </Stack>
          </MenuItem>
        </Box>
      </Menu>

      <Menu
        id="dashboard-user-menu"
        anchorEl={anchorElUser}
        open={Boolean(anchorElUser)}
        onClose={handleCloseUserMenu}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 286,
            borderRadius: 4,
            border: '1px solid rgba(122, 129, 151, 0.14)',
            backgroundColor: 'white',
            boxShadow: '0 18px 42px rgba(20, 32, 60, 0.12)',
            overflow: 'visible',
            p: 0.75,
            '& .MuiMenuItem-root': {
              borderRadius: 3,
              px: 1.25,
              py: 1,
              color: colors.cello,
              '&:hover': {
                backgroundColor: '#F6F8FC',
              },
            },
          },
        }}
        MenuListProps={{
          sx: {
            backgroundColor: 'white',
            p: 0,
          },
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <Box sx={{ px: 1.25, pt: 0.75, pb: 1 }}>
          <Typography sx={{ color: colors.cello, fontSize: '0.98rem', fontWeight: 800 }}>Asso & Membres</Typography>
          <Typography sx={{ color: '#7A8197', fontSize: '0.82rem', fontWeight: 500 }}>
            Accédez aux réglages de votre association, à vos membres et à vos outils.
          </Typography>
        </Box>
        <MenuItem onClick={() => handleNavigate(PATHS.association.absolute)}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <ApartmentRounded fontSize="small" />
            <Typography>Mon asso</Typography>
          </Stack>
        </MenuItem>

        <Box
          sx={{
            position: 'relative',
            pl: 1.25,
            py: 0.25,
            my: 0.25,
            backgroundColor: 'white',
            '& .MuiMenuItem-root': {
              backgroundColor: 'white',
              '&.Mui-focusVisible': { backgroundColor: 'white' },
              '&.Mui-selected': { backgroundColor: 'white' },
            },
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              bottom: 8,
              left: 14,
              width: 1,
              backgroundColor: '#D7DDEA',
              borderRadius: '999px',
            }}
          />

          <MenuItem onClick={() => handleNavigate(PATHS.suiviBenevole.absolute)} sx={{ pl: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <PeopleRounded fontSize="small" />
              <Typography>Membres</Typography>
            </Stack>
          </MenuItem>

          <MenuItem onClick={() => handleNavigate(PATHS.suiviVeterinaire.absolute)} sx={{ pl: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <MedicalServicesRounded fontSize="small" />
              <Typography>Vétérinaires</Typography>
            </Stack>
          </MenuItem>

          <MenuItem onClick={() => handleNavigate(PATHS.carte.absolute)} sx={{ pl: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <PlaceRounded fontSize="small" />
              <Typography>La carte</Typography>
            </Stack>
          </MenuItem>
        </Box>

        {customer?.stripeAccountId && (
          <MenuItem onClick={handleOpenStripeDashboard}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <PaymentsRounded fontSize="small" />
              <Typography>Stripe</Typography>
            </Stack>
          </MenuItem>
        )}

        {shouldShowAnimalExport && (
          <MenuItem onClick={handleOpenAnimalExportFromMenu}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <FileDownloadRounded fontSize="small" />
              <Typography>Export</Typography>
            </Stack>
          </MenuItem>
        )}
        {shouldShowMemberExport && <MemberExportSheet menuItem onBeforeOpen={handleCloseUserMenu} />}

        {hasConnectedAccountId && (
          <MenuItem
            onClick={() => handleNavigate(`${PATHS.suiviBenevole.absolute}/${accountsStore.connectedAccount?.id}`)}
          >
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <PersonRounded fontSize="small" />
              <Typography>{`Mon profil${connectedUserName ? ` (${connectedUserName})` : ''}`}</Typography>
            </Stack>
          </MenuItem>
        )}

        <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid rgba(122, 129, 151, 0.14)' }}>
          <MenuItem onClick={handleLogout}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <LogoutRounded fontSize="small" />
              <Typography>Se déconnecter</Typography>
            </Stack>
          </MenuItem>
        </Box>
      </Menu>

      <Menu
        id="dashboard-faq-menu"
        anchorEl={anchorElFaq}
        open={Boolean(anchorElFaq)}
        onClose={handleCloseFaqMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={handleOpenGettingStarted}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <QuestionAnswerRounded fontSize="small" />
            <Typography>Premiers pas</Typography>
          </Stack>
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleCloseFaqMenu()
            setIsVideoOpen(true)
          }}
        >
          Le tuto vidéo
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleCloseFaqMenu()
            navigate({ pathname: PATHS.suiviAdoptant.absolute, search: '?tab=settings' })
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <QuestionAnswerRounded fontSize="small" />
            <Typography>Procédure d&apos;adoption avec Petso</Typography>
          </Stack>
        </MenuItem>

        <MenuItem onClick={handleOpenPaymentsIntro}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <PaymentsRounded fontSize="small" />
            <Typography>Réception des frais d&apos;adoption avec Petso</Typography>
          </Stack>
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleCloseFaqMenu()
            window.open(REPORT_PROBLEM_URL, '_blank', 'noopener,noreferrer')
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <QuestionAnswerRounded fontSize="small" />
            <Typography>Reporter un problème</Typography>
          </Stack>
        </MenuItem>

        <MenuItem
          component="a"
          href="mailto:contact@petso.fr"
          onClick={handleCloseFaqMenu}
          target="_blank"
          rel="noreferrer"
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <QuestionAnswerRounded fontSize="small" />
            <Typography>Une question ? contact@petso.fr</Typography>
          </Stack>
        </MenuItem>
      </Menu>

      <GettingStartedModal open={isGettingStartedOpen} onClose={() => setIsGettingStartedOpen(false)} />
      <VideoPopIn forcedOpen={isVideoOpen} onRequestClose={() => setIsVideoOpen(false)} hideTrigger />

      {shouldShowAnimalExport && animalExportSignal != null && animalExportSignal > 0 && (
        <AnimalExportPopUp hideTrigger autoOpenSignal={animalExportSignal} />
      )}

      {/* Stripe Connect onboarding remains available from the legacy payments/settings screen. */}
    </>
  )
}
